import { create, type UseBoundStore, type StoreApi } from 'zustand'; // Import Zustand types
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware'; // Re-add persist imports
import type {
  DomainSuggestion,
  DomainVariation,
  // Removed unused: DomainDescription
} from '~/types/domain'; // Use type import
import type { Tld } from '~/config/tld-config'; // Import Tld type
import { TLD_VALUES } from '~/config/tld-lists'; // Import the actual TLD list

/* ---------- TYPES ---------- */
// Using DomainVariation directly from types/domain
// export interface Variation { ... } // Removed internal Variation type

export interface DomainEntity {
  id: string; // == baseName
  label: string; // e.g. "coolstartup"
  // Variations are now stored separately and linked by id/baseName
}

/* ---------- STORE SHAPE ---------- */
export interface DomainStoreState {
  // Added export
  // Domain Structure
  ids: string[]; // ordered list of baseNames (domain IDs)
  entities: Record<string, DomainEntity>; // baseName -> DomainEntity (without variations)
  variations: Record<string, DomainVariation[]>; // baseName -> List of its variations

  // Availability Cache (persisted)
  availabilityCache: Record<string, boolean>; // domainName (full) -> isAvailable

  // UI State (Renamed for clarity and consistency)
  isGenerating: boolean; // Loading suggestions (formerly searchLoading)
  isCheckingAvailability: boolean; // Loading availability checks (formerly availabilityLoading)
  apiError: string | null; // Formerly searchError
  searchTlds: Tld[]; // TLDs used in the last successful search

  // Removed description state
  /* Actions */
  upsertDomains(suggestions: DomainSuggestion[]): void;
  // Update signature to accept a result object
  patchAvailability(
    domainName: string,
    result: {
      available?: boolean;
      error?: boolean;
      errorMessage?: string;
      // Add other relevant fields if needed, e.g., price
    }
  ): void;
  setSearchTlds(tlds: Tld[]): void; // Action to set the TLDs used
  clearDomains(): void;
  setIsGenerating(isLoading: boolean): void; // Renamed action
  setIsCheckingAvailability(isLoading: boolean): void; // Renamed action
  setApiError(error: string | null): void; // Renamed action
  clearErrors(): void;
  clearAvailabilityCache(): void; // Action to clear only the availability cache
  // Removed description actions
  // Helper to get baseName from full domain name (implementation specific)
  _getBaseName(domainName: string): string | null;
}

/* ---------- UTILITY FUNCTIONS ---------- */
// Helper to extract base name, assuming TLDs don't contain dots
// This might need refinement based on actual TLD list (e.g., .co.uk)
const getBaseNameFromVariation = (
  domainName: string,
  allTlds: Set<string> // Pass known TLDs for better matching
): string | null => {
  // Sort TLDs by length descending to match longest first (e.g., .co.uk before .uk)
  const sortedTlds = Array.from(allTlds).sort((a, b) => b.length - a.length);
  for (const tld of sortedTlds) {
    if (domainName.endsWith(tld)) {
      return domainName.substring(0, domainName.length - tld.length);
    }
  }
  // console.warn(`Could not determine base name for: ${domainName}`); // Removed this log
  return null; // Or handle error appropriately
};

// Create a Set from the imported TLD_VALUES for efficient lookup
const allKnownTldsSet = new Set(TLD_VALUES);

/* ---------- STORE HOOK TYPE ---------- */
// Explicitly type the hook signature
// This helps ensure the middleware doesn't obscure the optional equality function parameter
type DomainStoreHook = UseBoundStore<StoreApi<DomainStoreState>>;

/* ---------- STORE IMPLEMENTATION ---------- */
export const useDomainStore = create<DomainStoreState>()(
  // <-- Wrap create call
  // Add type argument here
  persist(
    // Wrap with persist middleware
    immer(
      (set): DomainStoreState => ({
        // Explicitly type the return object
        // Remove unused 'get' parameter
        ids: [],
        entities: {},
        variations: {},
        availabilityCache: {}, // Initialize cache
        isGenerating: false, // Renamed state
        isCheckingAvailability: false, // Renamed state
        apiError: null, // Renamed state
        searchTlds: [], // Initialize search TLDs
        // Removed description state initialization

        upsertDomains(suggestions) {
          set((s) => {
            suggestions.forEach((suggestion) => {
              const { baseName, variations } = suggestion;
              if (!s.entities[baseName]) {
                s.ids.push(baseName);
                s.entities[baseName] = { id: baseName, label: baseName }; // Create entity if new
              }
              // Always update variations for the baseName
              s.variations[baseName] = variations.map((v) => ({
                ...v,
                // Apply cached status on upsert
                status:
                  s.availabilityCache[v.name] === true
                    ? 'available'
                    : s.availabilityCache[v.name] === false
                      ? 'unavailable'
                      : 'unchecked',
                available: s.availabilityCache[v.name],
                fromCache: s.availabilityCache[v.name] !== undefined,
              }));
            });
          });
        },

        setSearchTlds(tlds) {
          set({ searchTlds: tlds });
        },

        // Updated patchAvailability implementation
        patchAvailability(domainName, result) {
          // console.log(
          //   `[patchAvailability] Called for: ${domainName}, Result:`,
          //   result
          // ); // Log entry with full result // Removed this log
          set((s) => {
            // console.log(`[patchAvailability] Processing: ${domainName}`); // Log inside set // Removed this log

            // 1. Update the cache ONLY if availability is a boolean (not an error)
            if (typeof result.available === 'boolean') {
              s.availabilityCache[domainName] = result.available;
            } else if (result.error) {
              // If it's an error, we might want to remove it from cache or mark it differently
              // For now, let's just not update the cache with 'undefined'
              // console.log(
              //   `[patchAvailability] Error result for ${domainName}, not updating cache.`
              // ); // Removed this log
            }

            // 2. Update the UI state (find the variation in the `variations` slice)
            const baseName = s._getBaseName(domainName); // Use helper
            // console.log(`[patchAvailability] BaseName derived: ${baseName}`); // Log baseName // Removed this log

            // Log state before modification for this baseName
            // if (baseName && s.variations[baseName]) {
            //   console.log(
            //     `[patchAvailability] State for baseName '${baseName}' BEFORE patch:`,
            //     JSON.stringify(s.variations[baseName])
            //   );
            // } else {
            //   console.warn(
            //     `[patchAvailability] No baseName derived for ${domainName}.`
            //   );
            // } // Removed this log block

            if (baseName && s.variations[baseName]) {
              // Add check for entity existence
              if (!s.entities[baseName]) {
                // console.warn(
                //   `[patchAvailability] Entity missing for derived baseName '${baseName}' when processing ${domainName}.`
                // ); // Removed this log
              }
              const variationIndex = s.variations[baseName].findIndex(
                (v) => v.name === domainName
              );
              // console.log(
              //   `[patchAvailability] Found variationIndex for ${domainName}: ${variationIndex}`
              // ); // Log index // Removed this log
              if (variationIndex !== -1) {
                const variation = s.variations[baseName][variationIndex];

                // Update based on the result object
                variation.checking = false; // Mark as not checking anymore
                variation.fromCache = false; // Mark as fresh result

                if (result.error) {
                  variation.status = 'error';
                  variation.available = undefined; // Explicitly undefined on error
                  variation.error = true;
                  variation.errorMessage =
                    result.errorMessage || 'Check failed';
                  // console.warn(
                  //   `[patchAvailability] Marked ${domainName} as error: ${variation.errorMessage}`
                  // ); // Removed this log
                } else if (typeof result.available === 'boolean') {
                  variation.available = result.available;
                  variation.status = result.available
                    ? 'available'
                    : 'unavailable';
                  variation.error = false; // Clear error on successful check
                  variation.errorMessage = undefined; // Clear error message
                } else {
                  // Handle unexpected case where result is neither error nor boolean available
                  // console.error(
                  //   `[patchAvailability] Unexpected result format for ${domainName}:`,
                  //   result
                  // ); // Removed this log
                  variation.status = 'error'; // Mark as error
                  variation.available = undefined;
                  variation.error = true;
                  variation.errorMessage = 'Invalid result format';
                }

                // Log state after modification
                // console.log(
                //   `[patchAvailability] State for baseName '${baseName}' AFTER patch:`,
                //   JSON.stringify(s.variations[baseName])
                // ); // Removed this log
              } else {
                // console.error(
                //   // Changed to error for higher visibility
                //   `[patchAvailability] CRITICAL: Variation ${domainName} not found in s.variations['${baseName}']. Cannot update status. Current variations for this baseName:`,
                //   JSON.stringify(s.variations[baseName])
                // ); // Removed this log
              }
            } else {
              // Log if baseName or variations[baseName] is missing - Enhanced logging
              if (!baseName) {
                // console.error(
                //   // Changed to error
                //   `[patchAvailability] CRITICAL: Base name could not be derived for ${domainName}. Cannot update status.`
                // ); // Removed this log
              } else {
                // baseName exists, but s.variations[baseName] doesn't
                // console.error(
                //   // Changed to error
                //   `[patchAvailability] CRITICAL: s.variations['${baseName}'] is missing for domain ${domainName}. Cannot update status.`
                // ); // Removed this log
                // console.log(
                //   // Keep this log for context
                //   `[patchAvailability] Current variation keys in store: ${Object.keys(s.variations).join(', ')}`
                // ); // Removed this log
                // Also check if the entity exists
                if (!s.entities[baseName]) {
                  // console.warn(
                  //   `[patchAvailability] Additionally, entity for baseName '${baseName}' is also missing.`
                  // ); // Removed this log
                }
              }
            }
          });
        },

        clearDomains() {
          set(() => ({
            ids: [],
            entities: {},
            variations: {},
            // Keep availabilityCache persisted
            isGenerating: false, // Reset renamed state
            isCheckingAvailability: false, // Reset renamed state
            apiError: null, // Reset renamed state
            searchTlds: [], // Reset search TLDs
            // Removed clearing of description state
          }));
        },

        setIsGenerating(isLoading) {
          set({ isGenerating: isLoading }); // Use renamed state
        },

        setIsCheckingAvailability(isLoading) {
          set({ isCheckingAvailability: isLoading }); // Use renamed state
        },

        setApiError(error) {
          set({
            apiError: error, // Use renamed state
            isGenerating: false, // Reset loading on error
            isCheckingAvailability: false, // Reset loading on error
          });
        },

        clearErrors() {
          set({ apiError: null }); // Use renamed state
        },

        // Internal helper implementation
        _getBaseName(domainName) {
          // Use the utility function defined outside with the complete TLD set
          return getBaseNameFromVariation(domainName, allKnownTldsSet);
        },

        // Removed description action implementations

        clearAvailabilityCache() {
          set({ availabilityCache: {} }); // Reset only the cache
          // console.log('[DomainStore] Availability cache cleared.'); // Removed this log
        },
      })
    ),
    {
      name: 'domain-store-v1', // Storage key
      storage: createJSONStorage(() => sessionStorage), // Use sessionStorage
      partialize: (state) => ({
        availabilityCache: state.availabilityCache, // Only persist the cache
      }),
    }
  )
) as DomainStoreHook; // <-- Cast the result

// Helper function to map suggestions (can be used by SearchService)
// Not part of the store state/actions itself
export const mapSuggestionsToStoreUpdate = (
  suggestions: DomainSuggestion[]
): DomainSuggestion[] => {
  // Currently, the store's upsertDomains accepts DomainSuggestion[] directly.
  // If transformation were needed, it would happen here.
  // For now, it's an identity function, but kept for structure.
  return suggestions;
};
